import {
  assert_equal,
  assert_false,
  assert_string_hash_sha256,
  assert_string_object_id_not_empty,
  assert_true,
  assert_type_enum,
} from '@shared/asserts/utils_asserts';
import { assert_string_uuid_not_empty } from '@shared/asserts/utils_asserts_string';
import { CONSTANTS__shared } from '@shared/constants/CONSTANTS__shared';
import { ENUM__string_id_empty } from '@shared/constants/ENUM__string_id_empty';
import { DTO__image_asset } from '@shared/dto/+image/image_asset_base/DTO__image_asset';
import { DTO__image_size } from '@shared/dto/+image/image_asset_base/DTO__image_size';
import { MAP__serializable } from '@shared/dto/_base/MAP__serializable';
import { ENUM__dto_class_name } from '@shared/serialization/ENUM__dto_class_name';
import { MAP__field_data } from '@shared/types/types';
import { logw } from '@shared/utils/utils_logs';
import { DTO_GRAPH__instance } from '../../_graph/DTO_GRAPH__instance';
import { ENUM__field_connectable_type } from '../_base/_methods/ENUM__field_connectable_type';
import { DTO_META__field_info } from '../_base/DTO_META__field_info';
import { FIELD__base } from '../_base/FIELD__base';
import { DECORATOR__class_serialization_tested } from '../_primitives/_base/DECORATOR__class_serialization_tested';
import { DECORATOR__class_serialization_tested_for_hash } from '../_primitives/_base/DECORATOR__class_serialization_tested_for_hash';
import { DECORATOR__field_is_input } from '../_primitives/_base/DECORATOR__field_is_input';
import { DECORATOR__field_not_linked } from '../_primitives/_base/DECORATOR__field_not_linked';
import { ENUM__image_asset_category } from '../ENUM__image_asset_category';
import { ENUM__image_transform, ENUM_UTILS__image_transform } from './_enums/ENUM__image_transform';

//todo: validate image size original is not 0x0
//todo: validate color mode original for uploaded is not default
//if soruce type is generated - hash is not empty

@DECORATOR__class_serialization_tested
@DECORATOR__class_serialization_tested_for_hash
export class FIELD__image_asset extends FIELD__base {
  /*
   * STATIC FIELDS
   */

  static _class_name_static = ENUM__dto_class_name.GRAPH_FIELD_IMAGE_ASSET;

  /*
   *
   */
  static sort_by_hash(a: FIELD__image_asset, b: FIELD__image_asset) {
    if (a.hash_sha256_content == b.hash_sha256_content) {
      return a.meta.field_hash__value_full.localeCompare(b.meta.field_hash__value_full);
    }

    return a.hash_sha256_content.localeCompare(b.hash_sha256_content);
  }

  static options__transform_stretch(options: Partial<FIELD__image_asset>) {
    options.preprocessing_transform__current = ENUM__image_transform.COVER;
    options.preprocessing_transform__allowed = ENUM_UTILS__image_transform.get_all_except_none();
  }
  static options__transform_none(options: Partial<FIELD__image_asset>) {
    options.preprocessing_transform__current = ENUM__image_transform.NONE;
    options.preprocessing_transform__allowed = [ENUM__image_transform.NONE];
  }

  /*
   *
   */
  static init_as_field_transform_stretch<T_FIELD extends FIELD__base>(
    this: new () => T_FIELD,
    options_field: Partial<T_FIELD> = {},
    options_meta: Partial<DTO_META__field_info> = {},
  ) {
    FIELD__image_asset.options__transform_stretch(options_field as Partial<FIELD__image_asset>);

    return super.init_as_field(options_field, options_meta);
  }

  static init_as_input_transform_stretch<T_FIELD extends FIELD__base>(
    this: new () => T_FIELD,
    options_field: Partial<T_FIELD> = {},
    options_meta: Partial<DTO_META__field_info> = {},
  ) {
    FIELD__image_asset.options__transform_stretch(options_field as Partial<FIELD__image_asset>);

    return super.init_as_input(options_field, options_meta);
  }

  static init_as_input_transform_none<T_FIELD extends FIELD__base>(
    this: new () => T_FIELD,
    options_field: Partial<T_FIELD> = {},
    options_meta: Partial<DTO_META__field_info> = {},
  ) {
    FIELD__image_asset.options__transform_none(options_field as Partial<FIELD__image_asset>);

    return super.init_as_input(options_field, options_meta);
  }

  static init_as_output<T_FIELD extends FIELD__base>(
    this: new () => T_FIELD,
    options_field: Partial<T_FIELD> = {},
    options_meta: Partial<DTO_META__field_info> = {},
  ) {
    FIELD__image_asset.options__transform_none(options_field as Partial<FIELD__image_asset>);

    return super.init_as_output(options_field, options_meta);
  }

  /*
   * STATIC METHODS
   */
  static get_map_by_field_hash(fields: FIELD__base[]) {
    let out = new MAP__serializable<string, FIELD__image_asset[]>();

    for (let field of fields.values()) {
      if (!(field instanceof FIELD__image_asset)) continue;

      out.add_item_to_array(field.meta.field_hash__value_full, field);
    }

    return out;
  }
  static get_map_by_hash_content(fields: FIELD__base[]) {
    let out = new MAP__serializable<string, FIELD__image_asset[]>();

    for (let field of fields.values()) {
      if (!(field instanceof FIELD__image_asset)) continue;

      out.add_item_to_array(field.hash_sha256_content, field);
    }

    return out;
  }

  /*
   * META
   */

  //field category
  hash_sha256_content!: string;
  category!: ENUM__image_asset_category;

  asset_original__is_single_color!: boolean;
  asset_original__size!: DTO__image_size;

  preprocessing_transform__current!: ENUM__image_transform;
  preprocessing_transform__allowed!: ENUM__image_transform[];

  /*
   * FIELDS
   */

  /*
   * RUNTIME FIELDS
   */
  asset!: DTO__image_asset; //present only on client

  /*
   * PROPERTIES
   */

  get_image_size() {
    assert_true(this.get_has_content());
    assert_false(this.asset_original__size.is_stub());

    return this.asset_original__size;
  }

  @DECORATOR__field_not_linked()
  get_text__mask() {
    if (this.is_stub()) return '';
    assert_true(this.is_category_mask);

    let out = '';

    if (!this.get_has_content()) {
      out = this.meta.kind__is_output ? 'mask not generated yet' : 'mask not created yet';
    } else if (this.asset.is_single_color_mask) {
      out = 'full masked';
    } else if (this.asset.is_single_color_white) {
      out = 'no mask';
    } else {
      out = 'partial masked';
    }

    return out;
  }

  get is_category_mask() {
    return this.category == ENUM__image_asset_category.MASK;
  }

  get is_category_line() {
    let categories_line = [
      ENUM__image_asset_category.LINE__CANNY,
      ENUM__image_asset_category.LINE__LINEART,
      ENUM__image_asset_category.LINE__MLSD,
      ENUM__image_asset_category.LINE__SCRIBBLE,
      ENUM__image_asset_category.LINE__SOFT_EDGE,
    ];

    return categories_line.includes(this.category);
  }

  get is_category_image() {
    return this.category == ENUM__image_asset_category.IMAGE;
  }

  get is_category_any() {
    return this.category == ENUM__image_asset_category.ANY;
  }

  get graph_field__height() {
    if (this.meta.data__is_linked) return 1;

    return CONSTANTS__shared.GRAPH_FIELD_HEIGHT_IMAGE + 1;
  }

  get images_hashes_to_load(): Set<string> {
    let out = new Set<string>();

    if (!this.hash_sha256_content.is_empty_hash_sha256()) out.add(this.hash_sha256_content);

    return out;
  }

  field_connectable__type() {
    return ENUM__field_connectable_type.IMAGE_ASSET;
  }

  field_connectable__value(graph: DTO_GRAPH__instance): string {
    return this.hash_sha256_content.remove_string_protocol();
  }

  @DECORATOR__field_not_linked()
  get_has_content() {
    assert_false(this.is_stub());

    assert_string_hash_sha256(this.hash_sha256_content);

    return !this.hash_sha256_content.is_empty_hash_sha256();
  }

  get db_filter_to_find_image_meta() {
    const { graph_id, node_id, field_id } = this.meta;

    assert_string_object_id_not_empty(graph_id);
    assert_string_uuid_not_empty(node_id);
    assert_string_uuid_not_empty(field_id);

    return { graph_id, node_id, field_id };
  }

  /*
   * EVENTS
   */

  /*
   * METHODS
   */

  constructor() {
    super();

    this.hash_sha256_content = ENUM__string_id_empty.HASH_SHA256;

    this.asset = DTO__image_asset.CREATE_STUB();

    this.image__clear();

    this.preprocessing_transform__current = ENUM__image_transform.NONE;
    this.preprocessing_transform__allowed = Object.values(ENUM__image_transform);
  }

  post_init(): void {
    super.post_init();
  }

  /*
   * IMAGE META
   */

  @DECORATOR__field_not_linked()
  image__asset_use(image: DTO__image_asset) {
    assert_false(image.is_stub());

    this.hash_sha256_content = image.hash_sha256_content;

    if (image.category != this.category && image.category != ENUM__image_asset_category.ANY) {
      logw(`mismatch field and asset categories: ${this.category} and ${image.category}`);
    }

    this.asset = image.clone();
    this.asset.category = this.category;

    this.asset_original__size = image.image_size.clone();
    this.asset_original__is_single_color = image.is_single_color;
  }

  @DECORATOR__field_not_linked()
  image__clear() {
    this.hash_sha256_content = ENUM__string_id_empty.HASH_SHA256;

    this.asset = DTO__image_asset.CREATE_STUB();

    this.asset_original__size = DTO__image_size.CREATE_STUB();
  }

  @DECORATOR__field_not_linked()
  image__asset_stubify() {
    this.asset = DTO__image_asset.CREATE_STUB();
  }

  get image__is_initialized() {
    return !this.asset.is_stub();
  }

  get hash_base_values() {
    let out = super.hash_base_values;

    out.push(`category=${this.category}`);
    out.push(`preprocessing_transform__current=${this.preprocessing_transform__current}`);

    return out;
  }

  /*
   * HASH
   */

  /*
   * VALIDATIONS
   */

  is_field_is_compatible_to_sync_with(other: FIELD__base): boolean {
    if (!super.is_field_is_compatible_to_sync_with(other)) return false;
    if (!(other instanceof FIELD__image_asset)) return false;
    if (this.category == ENUM__image_asset_category.ANY) return true;
    if (other.category == ENUM__image_asset_category.ANY) return true;

    return this.category == other.category;
  }

  /*
   * SHARING
   */

  @DECORATOR__field_not_linked()
  store__deserialize_field_value(field_name: string, value_from_this: any, field_value: any): any {
    let out = super.store__deserialize_field_value(field_name, value_from_this, field_value);

    this.image__asset_stubify();

    return out;
  }

  @DECORATOR__field_not_linked()
  store__can_deserialize_from_data(map: MAP__field_data) {
    if (!super.store__can_deserialize_from_data(map)) return false;

    let category = map.get_or_default('category', this.category);
    if (category != this.category) return false;

    return true;
  }

  @DECORATOR__field_is_input()
  @DECORATOR__field_not_linked()
  store__serialization_keys_list_black(): string[] {
    let out: (keyof this)[] = super.store__serialization_keys_list_black() as (keyof this)[];

    out.push('asset');

    return out as string[];
  }

  @DECORATOR__field_is_input()
  @DECORATOR__field_not_linked()
  store__serialization_keys_list_white_for_hash(): string[] {
    let out: (keyof this)[] = [];

    out.push('hash_sha256_content');
    out.push('preprocessing_transform__current');

    return out as string[];
  }

  /*
   *
   */
  @DECORATOR__field_not_linked()
  has_effect_on_generation_runtime(graph: DTO_GRAPH__instance) {
    if (this.meta.kind__is_input && !this.get_has_content()) return false;
    if (this.meta.kind__is_input && !this.is_category_mask && this.asset_original__is_single_color) return false;
    if (this.meta.kind__is_output && this.meta.field_hash__calculated && this.meta.field_hash__value_full.is_empty_hash_sha256()) return false;

    return super.has_effect_on_generation_runtime(graph);
  }

  hash__create__case_input__only_core(graph: DTO_GRAPH__instance) {
    if (this.get_has_content()) {
      return this.hash_sha256_content;
    }

    return super.hash__create__case_input__only_core(graph);
  }

  /*
   * ASSERTS
   */

  assert_standard_fields(): void {
    super.assert_standard_fields();

    assert_type_enum(this.category, ENUM__image_asset_category);

    assert_string_hash_sha256(this.hash_sha256_content);

    assert_type_enum(this.preprocessing_transform__current, ENUM__image_transform);
  }

  assert_category_of_content(category: ENUM__image_asset_category) {
    if (category == ENUM__image_asset_category.ANY) return;
    if (this.category == ENUM__image_asset_category.ANY) return;

    assert_equal(this.category, category);
  }
}
